import React, { useEffect, useRef } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import classnames from 'classnames'
import find from 'lodash/find'
import hasElementOrSomeParentTheClass from '@Utils/HasElementOrSomeParentTheClass'
import Overlay from '@SiteComponents/Overlay'
import ProjectVersionsViewHeader from './ProjectVersionsViewHeader'
import ProjectVersionsTable from './ProjectVersionsTable'
import Close from '@Images/languages_view_close.svg'
import Expand from '@Images/languages_view_expand.svg'
import Next from '@Images/languages_view_next.svg'
import Previous from '@Images/languages_view_previous.svg'

import {
  IProject,
} from '@SiteModules/Project/types'

import {
  IOpenRenameProjectVersionPopupPayload,
  IOpenDeleteProjectVersionPopupPayload
} from '@SiteModules/Popup/types'

import {
  getProjectsWithLanguages,
  getProjectLabelCaptions,
} from '@SiteModules/Project/selectors'

import {
  getCurrentAccountHasCurrentSubscription
} from '@SiteModules/Account/selectors'

import {
  getAnyPopupThatCanBeOpenedFromProjectVersionsOrLanguagesViewIsOpen
} from '@SiteModules/Popup/selectors'

import {
  openSetupProjectPopup,
  openRenewSubscriptionPopup,
  openRenameProjectVersionPopup,
  openDeleteProjectVersionPopup,
} from '@SiteModules/Popup/actions'

import './ProjectVersionsView.scss'

interface ProjectVersionsViewProps {
  readonly project: IProject | undefined
  readonly fullProjectVersionsView: boolean
  readonly shouldRender: boolean
  resetOpenedProject(): void
  setOpenedProjectId(projectId: string): void
  setOpenedProjectVersionId(projectVersionId: string): void
  setFullProjectVersionsView(newFullProjectVersionsView: boolean): void
}

const ProjectVersionsView: React.FC<ProjectVersionsViewProps> = props => {
  const dispatch = useDispatch()

  const projectsWithLanguages = useSelector(getProjectsWithLanguages)
  const projectLabelCaptions = useSelector(getProjectLabelCaptions)
  const currentAccountHasCurrentSubscription = useSelector(getCurrentAccountHasCurrentSubscription)
  const anyPopupThatCanBeOpenedFromProjectVersionsOrLanguagesViewIsOpen = useSelector(getAnyPopupThatCanBeOpenedFromProjectVersionsOrLanguagesViewIsOpen)

  const {
    fullProjectVersionsView,
    project,
    shouldRender,
  } = props

  const halfProjectVersionsView = !fullProjectVersionsView && shouldRender
  const shouldRenderOverlay = halfProjectVersionsView
  const originalProjectVersion = project ? find(project.projectVersions, pv => !pv.attributes.isRepurposed) : undefined

  const projectsWithLanguagesRef = useRef(projectsWithLanguages)
  const indexOfOpenedProject = project ? projectsWithLanguages.indexOf(project) : -1
  const indexOfOpenedProjectRef = useRef(indexOfOpenedProject)
  const isFirstProjectWithLanguages = indexOfOpenedProject === 0
  const isLastProjectWithLanguages = indexOfOpenedProject === projectsWithLanguages.length - 1
  const fullProjectVersionsViewRef = useRef(fullProjectVersionsView)
  const anyPopupThatCanBeOpenedFromProjectVersionsOrLanguagesViewIsOpenRef = useRef(anyPopupThatCanBeOpenedFromProjectVersionsOrLanguagesViewIsOpen)

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (fullProjectVersionsViewRef.current || anyPopupThatCanBeOpenedFromProjectVersionsOrLanguagesViewIsOpenRef.current) { return }
      if (e.target) {
        const element = e.target as HTMLElement
        if (hasElementOrSomeParentTheClass(element, 'ProjectVersionsView__element')) {
          return
        }
        props.resetOpenedProject()
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    document.addEventListener('keydown', handleKeyDown)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [])

  useEffect(() => {
    indexOfOpenedProjectRef.current = indexOfOpenedProject
  }, [indexOfOpenedProject])

  useEffect(() => {
    projectsWithLanguagesRef.current = projectsWithLanguages
  }, [projectsWithLanguages])

  useEffect(() => {
    fullProjectVersionsViewRef.current = fullProjectVersionsView
  }, [fullProjectVersionsView])

  useEffect(() => {
    anyPopupThatCanBeOpenedFromProjectVersionsOrLanguagesViewIsOpenRef.current = anyPopupThatCanBeOpenedFromProjectVersionsOrLanguagesViewIsOpen
  }, [anyPopupThatCanBeOpenedFromProjectVersionsOrLanguagesViewIsOpen])

  useEffect(() => {
    if (!shouldRender || window.innerWidth > 900) { return }

    window.scrollTo({ left: 0 })
    document.documentElement.scrollLeft = 0
    document.body.scrollLeft = 0
  }, [shouldRender, project?.id])

  const isArrowUp = (event: KeyboardEvent) => {
    return event.key === 'ArrowUp'
  }

  const isArrowDown = (event: KeyboardEvent) => {
    return event.key === 'ArrowDown'
  }

  const handleKeyDown = (event: KeyboardEvent) => {
    if (isArrowUp(event)) {
      event.preventDefault()
      setPreviousProjectWithLanguages()
    }

    if (isArrowDown(event)) {
      event.preventDefault()
      setNextProjectWithLanguages()
    }
  }

  const handleOpenSetupProjectOrRenewSubscriptionPopup = (projectVersionId: string, projectType?: string) => {
    if (currentAccountHasCurrentSubscription) {
      dispatch(openSetupProjectPopup({ projectVersionIds: [projectVersionId], projectType }))
    } else {
      dispatch(openRenewSubscriptionPopup())
    }
  }

  const handleOpenDeleteProjectVersionPopup = (payload: IOpenDeleteProjectVersionPopupPayload) => {
    dispatch(openDeleteProjectVersionPopup(payload))
  }

  const handleOpenRenameProjectVersionPopup = (payload: IOpenRenameProjectVersionPopupPayload) => {
    dispatch(openRenameProjectVersionPopup(payload))
  }

  const setNextProjectWithLanguages = () => {
    if (indexOfOpenedProjectRef.current < projectsWithLanguagesRef.current.length - 1 && indexOfOpenedProjectRef.current > -1) {
      const nextProjectWithLanguages = projectsWithLanguagesRef.current[indexOfOpenedProjectRef.current + 1]
      if (nextProjectWithLanguages.projectVersions.length > 1) {
        props.setOpenedProjectId(nextProjectWithLanguages.id)
      } else {
        props.resetOpenedProject()
        props.setOpenedProjectVersionId(nextProjectWithLanguages.projectVersions[0].id)
      }
    }
  }

  const setPreviousProjectWithLanguages = () => {
    if (indexOfOpenedProjectRef.current > 0) {
      const previousProjectWithLanguages = projectsWithLanguagesRef.current[indexOfOpenedProjectRef.current - 1]
      if (previousProjectWithLanguages.projectVersions.length > 1) {
        props.setOpenedProjectId(previousProjectWithLanguages.id)
      } else {
        props.resetOpenedProject()
        props.setOpenedProjectVersionId(previousProjectWithLanguages.projectVersions[0].id)
      }
    }
  }

  const renderActionsHalfView = () => {
    return (
      <div className="ProjectVersionsView__actions">
        <Close
          className="ProjectVersionsView__actions-close"
          onClick={props.resetOpenedProject}
        />
        <Expand
          className="ProjectVersionsView__actions-expand"
          onClick={props.setFullProjectVersionsView}
        />
        <div className="ProjectVersionsView__actions-separator"/>
        <Next
          className={classnames(
            'ProjectVersionsView__actions-next',
            { 'ProjectVersionsView__actions-next-disabled': isLastProjectWithLanguages }
          )}
          onClick={setNextProjectWithLanguages}
        />
        <Previous
          className={classnames(
            'ProjectVersionsView__actions-next',
            { 'ProjectVersionsView__actions-previous-disabled': isFirstProjectWithLanguages }
          )}
          onClick={setPreviousProjectWithLanguages}
        />
      </div>
    )
  }

  const renderHeader = () => {
    return (
      <ProjectVersionsViewHeader
        fullProjectVersionsView={fullProjectVersionsView}
        project={project}
        originalProjectVersion={originalProjectVersion}
        projectLabelCaptions={projectLabelCaptions}
        resetOpenedProject={props.resetOpenedProject}
        handleOpenSetupProjectOrRenewSubscriptionPopup={handleOpenSetupProjectOrRenewSubscriptionPopup}
      />
    )
  }

  const renderProjectVersionsTable = () => {
    return (
      <ProjectVersionsTable
        projectVersions={(project as IProject).projectVersions}
        fullProjectVersionsView={fullProjectVersionsView}
        setOpenedProjectVersionId={props.setOpenedProjectVersionId}
        resetOpenedProject={props.resetOpenedProject}
        handleOpenRenameProjectVersionPopup={handleOpenRenameProjectVersionPopup}
        handleOpenDeleteProjectVersionPopup={handleOpenDeleteProjectVersionPopup}
        handleOpenSetupProjectOrRenewSubscriptionPopup={handleOpenSetupProjectOrRenewSubscriptionPopup}
      />
    )
  }

  return (
    <React.Fragment>
      {shouldRenderOverlay && (
        <Overlay />
      )}
      <div className={classnames(
        'ProjectVersionsView',
        'ProjectVersionsView__element',
        { 'ProjectVersionsView__hidden': !shouldRender },
        { 'ProjectVersionsView__full': fullProjectVersionsView }
      )}>
        {halfProjectVersionsView && renderActionsHalfView()}
        {renderHeader()}
        {project && renderProjectVersionsTable()}
      </div>
    </React.Fragment>
  )
}

export default ProjectVersionsView
